import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@revelio/ui';

import { CustomColumnModal } from '../../components/custom-columns/CustomColumnModal';
import {
  AddCustomColumnOption,
  CustomColumnCard,
} from '../../components/custom-columns/CustomColumnOption';
import { DataBuilderGrid } from '../../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../../components/ui/DataBuilderOption';
import { LabeledGroup } from '../../components/ui/LabeledGroup';
import type { DatasetInstanceConfig } from '../../data-builder.store';
import { useDataBuilderActions } from '../../data-builder.store';
import type {
  AggregationColumnItem,
  AggregationColumns,
  ColumnOption,
} from '../../datasets/deliverable-column-config';
import { STEP2_VALIDATION_SECTION_AGGREGATION } from '../step2-columns-validation';
import { isAggregationGroupFullyDisabled } from './get-disabled-aggregation-keys';
import { ColumnOptionCard } from './SelectionOptions';
import { Step2SectionValidationError } from './Step2SectionValidationError';
import { useResolveFilterLabels } from './useResolveFilterLabels';

const AGGREGATION_BREAKDOWN_ID = 'aggregation';

type AggregationColumnsSectionProps = {
  aggregationColumns: AggregationColumns;
  selectedColumnKeys: string[];
  disabledColumnKeys: Set<string>;
  activeConfig: DatasetInstanceConfig;
  activeInstanceId: string | null;
  onSetColumnKey: (key: string, selected: boolean) => void;
  showValidationErrors: boolean;
  sectionErrorMessage: string | undefined;
  showRowLimitError: boolean;
};

export const AggregationColumnsSection = ({
  aggregationColumns,
  selectedColumnKeys,
  disabledColumnKeys,
  activeConfig,
  activeInstanceId,
  onSetColumnKey,
  showValidationErrors,
  sectionErrorMessage,
  showRowLimitError,
}: AggregationColumnsSectionProps) => {
  const { addCustomColumn, updateCustomColumn, removeCustomColumn } =
    useDataBuilderActions();
  const [openGroupName, setOpenGroupName] = useState<string | null>(null);
  const [customColumnModalState, setCustomColumnModalState] = useState<
    { mode: 'add' } | { mode: 'edit'; index: number } | null
  >(null);

  const openGroup =
    openGroupName !== null
      ? aggregationColumns.items.find(
          (item): item is AggregationColumnItem & { type: 'group' } =>
            item.type === 'group' && item.name === openGroupName
        )
      : undefined;
  const customColumns =
    activeConfig.customColumnsByBreakdown[AGGREGATION_BREAKDOWN_ID] ?? [];
  const editingCustomColumn =
    customColumnModalState?.mode === 'edit'
      ? customColumns[customColumnModalState.index]
      : undefined;

  useResolveFilterLabels({
    filterDefinitions: aggregationColumns.customColumnFilterDefinitions,
    customColumns,
    onUpdateCustomColumn: (index, column) => {
      if (activeInstanceId !== null) {
        updateCustomColumn(
          activeInstanceId,
          AGGREGATION_BREAKDOWN_ID,
          index,
          column
        );
      }
    },
  });

  return (
    <section data-testid="data-builder-aggregation-columns">
      <LabeledGroup groupName="Levels of Aggregation">
        <DataBuilderGrid columns="responsive">
          {aggregationColumns.items.map((item) => {
            if (item.type === 'column') {
              const isSelected = selectedColumnKeys.includes(item.option.key);
              return (
                <ColumnOptionCard
                  key={item.option.key}
                  keyId={item.option.key}
                  label={item.option.label}
                  description={item.option.description}
                  isRequired={item.option.isRequired}
                  isDisabled={disabledColumnKeys.has(item.option.key)}
                  isSelected={isSelected}
                  onToggle={() => onSetColumnKey(item.option.key, !isSelected)}
                />
              );
            }

            const selectedCount = item.options.filter((option) =>
              selectedColumnKeys.includes(option.key)
            ).length;

            return (
              <ColumnGroupCard
                key={item.name}
                name={item.name}
                description={item.description}
                selectedCount={selectedCount}
                totalCount={item.options.length}
                isDisabled={isAggregationGroupFullyDisabled(
                  item.options,
                  disabledColumnKeys
                )}
                onClick={() => setOpenGroupName(item.name)}
              />
            );
          })}
          {aggregationColumns.supportsCustomColumns &&
            customColumns.map((customColumn, index) => (
              <CustomColumnCard
                key={customColumn.id}
                column={customColumn}
                onClick={() =>
                  setCustomColumnModalState({ mode: 'edit', index: index })
                }
                onRemove={() => {
                  if (activeInstanceId !== null) {
                    removeCustomColumn(
                      activeInstanceId,
                      AGGREGATION_BREAKDOWN_ID,
                      index
                    );
                  }
                }}
              />
            ))}
          {aggregationColumns.supportsCustomColumns && (
            <AddCustomColumnOption
              onClick={() => setCustomColumnModalState({ mode: 'add' })}
            />
          )}
        </DataBuilderGrid>
      </LabeledGroup>

      <Step2SectionValidationError
        sectionId={STEP2_VALIDATION_SECTION_AGGREGATION}
        message={sectionErrorMessage}
        show={showValidationErrors || showRowLimitError}
      />

      <ColumnGroupModal
        isOpen={openGroup !== undefined}
        groupName={openGroup?.name ?? ''}
        options={openGroup?.options ?? []}
        selectedColumnKeys={selectedColumnKeys}
        disabledColumnKeys={disabledColumnKeys}
        maxSelected={openGroup?.maxSelected ?? null}
        onSetColumnKey={onSetColumnKey}
        onClose={() => setOpenGroupName(null)}
      />
      {aggregationColumns.supportsCustomColumns && (
        <CustomColumnModal
          isOpen={customColumnModalState !== null}
          onClose={() => setCustomColumnModalState(null)}
          availableFilters={aggregationColumns.customColumnFilterDefinitions}
          availableFilterCategories={
            aggregationColumns.customColumnFilterCategories
          }
          existingColumn={editingCustomColumn}
          existingColumnNames={customColumns
            .filter((_, idx) => {
              if (customColumnModalState?.mode !== 'edit') {
                return true;
              }
              return idx !== customColumnModalState.index;
            })
            .map((column) => column.name)}
          onSubmit={(column) => {
            if (activeInstanceId === null) {
              return;
            }
            if (customColumnModalState?.mode === 'edit') {
              updateCustomColumn(
                activeInstanceId,
                AGGREGATION_BREAKDOWN_ID,
                customColumnModalState.index,
                column
              );
              return;
            }
            addCustomColumn(activeInstanceId, AGGREGATION_BREAKDOWN_ID, column);
          }}
        />
      )}
    </section>
  );
};

type ColumnGroupCardProps = {
  name: string;
  description?: string | null;
  selectedCount: number;
  totalCount: number;
  isDisabled?: boolean;
  onClick: () => void;
};

export function ColumnGroupCard({
  name,
  description,
  selectedCount,
  totalCount,
  isDisabled = false,
  onClick,
}: ColumnGroupCardProps) {
  const hasSelections = selectedCount > 0;
  return (
    <DataBuilderOption
      data-testid={`data-builder-column-group-${name}`}
      isSelected={hasSelections}
      disabled={isDisabled}
      onClick={onClick}
      startSlot={<DataBuilderOption.CheckBox isSelected={hasSelections} />}
      endSlot={
        <Flex alignItems="center" gap={2}>
          {description && (
            <DataBuilderOption.DescriptionTooltip description={description} />
          )}
          <DataBuilderOption.Chevron />
        </Flex>
      }
      endSlotAlignment="center"
    >
      <DataBuilderOption.Label>{name}</DataBuilderOption.Label>
      <DataBuilderOption.Description>
        {`${selectedCount}/${totalCount} selected`}
      </DataBuilderOption.Description>
    </DataBuilderOption>
  );
}

type ColumnGroupModalProps = {
  isOpen: boolean;
  groupName: string;
  options: ColumnOption[];
  selectedColumnKeys: string[];
  disabledColumnKeys?: Set<string>;
  maxSelected?: number | null;
  onSetColumnKey: (key: string, selected: boolean) => void;
  onClose: () => void;
};

export function ColumnGroupModal({
  isOpen,
  groupName,
  options,
  selectedColumnKeys,
  disabledColumnKeys,
  maxSelected,
  onSetColumnKey,
  onClose,
}: ColumnGroupModalProps) {
  const toast = useToast();
  const [draftKeys, setDraftKeys] = useState<Set<string>>(
    () => new Set(selectedColumnKeys)
  );
  const draftKeysRef = useRef(draftKeys);
  draftKeysRef.current = draftKeys;
  const selectedColumnKeysRef = useRef(selectedColumnKeys);
  selectedColumnKeysRef.current = selectedColumnKeys;

  useEffect(() => {
    if (isOpen) {
      setDraftKeys(new Set(selectedColumnKeysRef.current));
    }
  }, [isOpen]);

  const setDraftKey = (key: string, selected: boolean) => {
    setDraftKeys((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const handleToggleOption = (
    option: ColumnOption,
    isCurrentlySelected: boolean
  ) => {
    if (isCurrentlySelected) {
      setDraftKey(option.key, false);
      return;
    }
    if (maxSelected === 1) {
      setDraftKeys((prev) => {
        const next = new Set(prev);
        for (const candidate of options) {
          if (candidate.key === option.key) continue;
          if (candidate.isRequired) continue;
          if (disabledColumnKeys?.has(candidate.key)) continue;
          next.delete(candidate.key);
        }
        next.add(option.key);
        return next;
      });
      return;
    }
    if (typeof maxSelected === 'number' && maxSelected > 1) {
      const eligibleSelected = options.filter(
        (candidate) =>
          draftKeys.has(candidate.key) &&
          !candidate.isRequired &&
          !disabledColumnKeys?.has(candidate.key)
      ).length;
      if (eligibleSelected >= maxSelected) {
        toast({
          title: `You can only select up to ${maxSelected} ${groupName}`,
          status: 'info',
          duration: 4000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }
    }
    setDraftKey(option.key, true);
  };

  const handleClearSelection = () => {
    setDraftKeys((prev) => {
      const next = new Set(prev);
      options.forEach((option) => {
        if (option.isRequired || disabledColumnKeys?.has(option.key)) {
          return;
        }
        next.delete(option.key);
      });
      return next;
    });
  };

  const commitDraft = () => {
    const currentDraft = draftKeysRef.current;
    const currentSelected = selectedColumnKeysRef.current;
    options.forEach((option) => {
      if (option.isRequired || disabledColumnKeys?.has(option.key)) {
        return;
      }
      const wasSelected = currentSelected.includes(option.key);
      const isSelectedInDraft = currentDraft.has(option.key);
      if (wasSelected !== isSelectedInDraft) {
        onSetColumnKey(option.key, isSelectedInDraft);
      }
    });
  };

  const handleSaveAndClose = () => {
    commitDraft();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleSaveAndClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent
        data-testid={`data-builder-column-group-modal-${groupName}`}
      >
        <ModalHeader color="text.primary" fontWeight="semibold" fontSize="md">
          {groupName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <DataBuilderGrid columns="2">
            {options.map((option) => {
              const isSelected = draftKeys.has(option.key);
              return (
                <ColumnOptionCard
                  key={option.key}
                  keyId={option.key}
                  label={option.label}
                  description={option.description}
                  isRequired={option.isRequired}
                  isDisabled={disabledColumnKeys?.has(option.key) ?? false}
                  isSelected={isSelected}
                  onToggle={() => handleToggleOption(option, isSelected)}
                />
              );
            })}
          </DataBuilderGrid>
        </ModalBody>
        <ModalFooter justifyContent="space-between" gap={2}>
          <Button
            variant="ghost"
            color="destructive"
            size="sm"
            onClick={handleClearSelection}
          >
            Clear Selection
          </Button>
          <Flex gap={2}>
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button color="primary" size="sm" onClick={handleSaveAndClose}>
              Save
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
